import { FC, useEffect, useRef, useState } from 'react';
import { FormErrors } from '../../constants/errorMessages';
import { AlertSVG, DropDownSVG } from '../svgs';
import { FilterOption } from './FilterDropdown';
import { INVENTORY_MANAGEMENT_CONSTANTS } from '../../constants/common';

type MultiSelectDropdownProps<T> = {
  id?: string;
  options: FilterOption<T>[];
  value: FilterOption<T>[];
  onChange: (selected: FilterOption<T>[]) => void;
  validate?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
};

const MultiSelectDropdown = <T,>({
  options,
  value,
  onChange,
  validate = false,
  searchPlaceholder = 'Search...',
  placeholder = 'Choose One or More',
  disabled = false,
}: MultiSelectDropdownProps<T>): JSX.Element => {
  const [input, setInput] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [highlighted, setHighlighted] = useState<number>(-1);
  const [filteredOptions, setFilteredOptions] = useState<FilterOption<T>[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState<boolean>(true);

  const focus = () => {
    setInput('');
    setHighlighted(-1);
    setHidden(false);
    inputRef.current?.focus();
  };

  const toggleItem = (option: FilterOption<T>) => {
    const exists = value.some((selectedOption: FilterOption<T>) => selectedOption.value === option.value);
    const newSelected = exists
      ? value.filter((selectedOption: FilterOption<T>) => selectedOption.value !== option.value)
      : [...value, option];

    onChange(newSelected);
  };

  const getFilteredOptions = (): FilterOption<any>[] => {
    return options.filter(item => item.name.toLowerCase().includes(input.toLowerCase()));
  };

  const updateInput = (inputString: string) => {
    setInput(inputString);
    setFilteredOptions(getFilteredOptions());
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!hidden) {
      const filteredOptionsArray = getFilteredOptions();
      switch (e.key) {
        case 'ArrowDown':
          setHighlighted(prev => Math.min(prev + 1, filteredOptionsArray.length - 1));
          break;
        case 'ArrowUp':
          setHighlighted(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          if (highlighted >= 0 && filteredOptionsArray[highlighted]) toggleItem(filteredOptionsArray[highlighted]);
          break;
      }
    }
  };

  useEffect(() => {
    setFilteredOptions(getFilteredOptions());
  }, [input, options]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [hidden]);

  const formatLabel = (label: string): JSX.Element => {
    return label.includes('	') ? (
      <p className="flex-apart">
        {label.split('	').map((text, i) => (
          <span key={text + i}>{text}</span>
        ))}
      </p>
    ) : (
      <p>{label}</p>
    );
  };

  return (
    <>
      <div className={disabled ? 'filter-dropdown disabled' : 'filter-dropdown'} onClick={() => !disabled && focus()}>
        <div className="form-field">
          {placeholder}
          <DropDownSVG />
        </div>

        {!disabled && (
          <div className={hidden ? 'options hidden' : 'options'} ref={scrollRef}>
            <input
              className="filter-input"
              type="text"
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={input}
              onChange={e => updateInput(e.target.value)}
              onFocus={focus}
              onBlur={() => setHidden(true)}
              onKeyDownCapture={handleKeypress}
            />

            {filteredOptions.length > 0 ? (
              filteredOptions.map((item, index) => {
                const isSelected = value.some((selectedOption: FilterOption<T>) => selectedOption.value === item.value);

                return (
                  <div
                    className={`option ${isSelected ? 'option-selected' : ''} ${index === highlighted ? 'highlighted' : ''} ${
                      item.optionDisabled ? 'option-disabled' : item.optionWarning ? 'option-warning' : ''
                    }`}
                    key={`${String(item.value)}-${index}`}
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleItem(item);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={isSelected} readOnly />
                    {item.optionDisabled || item.optionWarning ? (
                      <>
                        {formatLabel(item.name)} ({item.message})
                      </>
                    ) : (
                      formatLabel(item.name)
                    )}
                  </div>
                );
              })
            ) : (
              <div className="no-results">{INVENTORY_MANAGEMENT_CONSTANTS.NO_OPTIONS}</div>
            )}
          </div>
        )}
      </div>

      {validate && value.length === 0 && (
        <div className="validated-input-message-error">
          <AlertSVG /> {FormErrors[0].message}
        </div>
      )}
    </>
  );
};

export default MultiSelectDropdown;
